import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AudioReactiveParticles from '../components/common/AudioReactiveParticles';
import { onboardingApi, handleOnboardingError, ResumeUploadData } from '../api/onboardingApi';
import { vibeService } from '../services/VibeService';
import './OnboardingResume.css';

type UploadState = 'idle' | 'dragging' | 'uploading' | 'success' | 'error' | 'loading';

const OnboardingResume: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [uploadState, setUploadState] = useState<UploadState>('loading');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedData, setUploadedData] = useState<ResumeUploadData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Animation states
  const [showAnimation, setShowAnimation] = useState(false);
  const [triggerFormation, setTriggerFormation] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [uiFadingOut, setUiFadingOut] = useState(false);
  
  // Logo states
  const [showLogo, setShowLogo] = useState(false); // Top-left logo during audio
  const [logoFadingOut, setLogoFadingOut] = useState(false); // Logo fade-out animation
  const [showFinalLogo, setShowFinalLogo] = useState(false); // Center logo after scatter

  // Redirect to preferences if they haven't been completed yet
  useEffect(() => {
    const preferencesCompleted = localStorage.getItem('preferencesCompleted');
    if (!preferencesCompleted) {
      navigate('/onboarding/preferences', { replace: true });
    }
  }, [navigate]);

  // Check if user already has a resume on mount
  useEffect(() => {
    const checkExistingResume = async () => {
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        const { has_resume, resume_summary } = profileResponse.data;
        
        if (has_resume && resume_summary) {
          // User already has a resume - show success state with existing data
          // Fetch full resume data for more details
          try {
            const resumeResponse = await onboardingApi.getResume();
            const resumeData = resumeResponse.data;
            
            // Map resume data to upload data format
            setUploadedData({
              s3_url: resumeData.s3_url,
              original_filename: resumeData.original_filename,
              num_pages: resumeData.num_pages,
              contact_details: resumeData.parsed_data?.contact_details || {
                name: '',
                email: '',
                phone: '',
                linkedin: ''
              },
              skills_count: resumeData.parsed_data?.skills_and_achievements?.extracted_skills?.length || 0,
              experience_count: resumeData.parsed_data?.experience?.entries?.length || 0,
              education_count: resumeData.parsed_data?.education?.entries?.length || 0
            });
            setUploadState('success');
          } catch {
            // If we can't get full resume data, use summary
            setUploadedData({
              s3_url: '',
              original_filename: resume_summary.original_filename,
              num_pages: resume_summary.num_pages,
              contact_details: { name: '', email: '', phone: '', linkedin: '' },
              skills_count: 0,
              experience_count: 0,
              education_count: 0
            });
            setUploadState('success');
          }
        } else {
          // No resume yet - show upload UI
          setUploadState('idle');
        }
      } catch (error) {
        // If profile fetch fails, default to upload UI
        console.error('Failed to check existing resume:', error);
        setUploadState('idle');
      }
    };

    checkExistingResume();
  }, []);

  const validateFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') {
      return 'Only PDF files are supported. Please select a PDF file.';
    }
    if (file.size > 10 * 1024 * 1024) {
      return 'File size exceeds 10MB. Please select a smaller file.';
    }
    return null;
  };

  const handleUpload = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setErrorMessage(validationError);
      setUploadState('error');
      return;
    }

    setSelectedFile(file);
    setUploadState('uploading');
    setUploadProgress(0);
    setErrorMessage(null);

    try {
      const response = await onboardingApi.uploadResume(file, (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setUploadedData(response.data);
      setUploadState('success');
      vibeService.trigger('celebration');
      
      // Start the animation sequence after successful upload
      startAnimationSequence();
    } catch (error) {
      const message = handleOnboardingError(error);
      setErrorMessage(message);
      setUploadState('error');
      vibeService.trigger('error');
    }
  };

  // Start the audio animation sequence
  const startAnimationSequence = () => {
    // Show particles immediately (in scattered state)
    setShowAnimation(true);
    setUiFadingOut(true);
    
    // 2 second delay: UI fades out, particles appear scattered, then form sphere
    setTimeout(() => {
      setTriggerFormation(true);
      setShowLogo(true); // Show logo when formation/audio starts
    }, 2000);
  };

  // Handle Audio 1 end - show button (subtle, below sphere)
  const handleAudio1End = useCallback(() => {
    setButtonVisible(true);
  }, []);

  // Handle Audio 2 end - enable button, fade out top logo, show center logo
  const handleAudio2End = useCallback(() => {
    setLogoFadingOut(true); // Start fade-out animation
    setButtonEnabled(true);
    
    // Hide top logo after fade-out animation completes
    setTimeout(() => {
      setShowLogo(false);
      setLogoFadingOut(false);
    }, 800); // Match fade-out animation duration
    
    // Show center logo after scatter animation completes
    setTimeout(() => {
      setShowFinalLogo(true);
    }, 500);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadState !== 'uploading' && uploadState !== 'success') {
      setUploadState('dragging');
    }
  }, [uploadState]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadState === 'dragging') {
      setUploadState('idle');
    }
  }, [uploadState]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (uploadState === 'uploading' || uploadState === 'success') {
      return;
    }

    setUploadState('idle');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadState]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleUpload(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRetry = () => {
    setUploadState('idle');
    setSelectedFile(null);
    setErrorMessage(null);
    setUploadProgress(0);
    // Reset animation states
    setShowAnimation(false);
    setTriggerFormation(false);
    setButtonVisible(false);
    setButtonEnabled(false);
    setUiFadingOut(false);
    // Reset logo states
    setShowLogo(false);
    setLogoFadingOut(false);
    setShowFinalLogo(false);
  };

  const handleStartInterview = () => {
    // Navigate to first AI interview with onboarding flag
    navigate('/interview', { state: { isOnboarding: true } });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="onboarding-resume">
      {/* Background */}
      <div className="onboarding-background">
        {showAnimation && (
          <AudioReactiveParticles
            audioURL1="/Luci_intro.mp3"
            audioURL2="/Luci_interview_prompt.mp3"
            onAudio1End={handleAudio1End}
            onAudio2End={handleAudio2End}
            initialPhase="scattered"
            triggerFormation={triggerFormation}
            backgroundMode={false}
          />
        )}
      </div>

      {/* Top-left logo during audio playback */}
      {showLogo && (
        <div className={`audio-logo-top-left ${logoFadingOut ? 'fade-out' : 'fade-in'}`}>
          <img src="/image_i_do1.png" alt="Luci" />
        </div>
      )}

      {/* Center logo and message after scatter */}
      {showFinalLogo && (
        <div className="final-logo-container fade-in">
          <img src="/image_i_do1.png" alt="Luci" className="final-logo-image" />
          <p className="final-logo-text">Begin your journey with your first AI interview</p>
        </div>
      )}

      {/* Button at bottom */}
      {buttonVisible && (
        <div className="interview-button-overlay below-sphere">
          <button 
            className={`start-interview-button ${buttonEnabled ? 'active' : 'subtle'}`}
            onClick={handleStartInterview}
            disabled={!buttonEnabled}
          >
            <span>Begin Journey</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      )}

      {/* Split Layout Container - fade out when animation starts */}
      <div className={`onboarding-split-container ${uiFadingOut ? 'fade-out' : ''}`}>
        {/* Left Side - Welcome Message */}
        <div className="onboarding-left">
          <div className="welcome-content">
            <div className="onboarding-logo">
              <img src="/image_i_do1.png" alt="Luci logo" className="luci-logo" />
            </div>
            <h1 className="welcome-title">You're new here!</h1>
            <p className="welcome-subtitle">
              Please create your account by uploading your resume and help us analyze your profile.
            </p>
            <p className="welcome-description">
              Luci provides fully personalized career preparation through AI-driven interviews, tracking your progress continuously to help you improve.
            </p>
            
            {/* Step Indicator on left side */}
            <div className="step-indicator-vertical">
              <div className="step-v completed">
                <div className="step-dot-v" />
                <span>Preferences</span>
              </div>
              <div className="step-line-v" />
              <div className={`step-v ${uploadState === 'success' ? 'completed' : 'active'}`}>
                <div className="step-dot-v" />
                <span>Upload Resume</span>
              </div>
              <div className="step-line-v" />
              <div className={`step-v ${uploadState === 'success' ? 'active' : ''}`}>
                <div className="step-dot-v" />
                <span>AI Interview</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Upload & Actions */}
        <div className="onboarding-right">
          <div className="onboarding-card">
            {/* Loading State */}
            {uploadState === 'loading' && (
              <div className="loading-container">
                <div className="loading-spinner-large"></div>
                <p>Checking your profile...</p>
              </div>
            )}

            {uploadState !== 'loading' && (
              <div className="onboarding-card-header">
                <h1>{uploadState === 'success' ? 'Resume Ready!' : 'Upload Your Resume'}</h1>
                <p>
                  {uploadState === 'success' 
                    ? 'Your resume is ready. Start your first AI interview to get personalized feedback.'
                    : "We'll analyze it to provide personalized interview preparation."
                  }
                </p>
              </div>
            )}

            {/* Upload Zone */}
            {(uploadState === 'idle' || uploadState === 'dragging') && (
              <div
                className={`upload-zone ${uploadState === 'dragging' ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBrowseClick}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                <div className="upload-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <div className="upload-text">
                  <span className="upload-primary">Drag and drop your resume here</span>
                  <span className="upload-secondary">or <button className="browse-link">browse files</button></span>
                </div>
                <div className="upload-hint">
                  PDF files only, max 10MB
                </div>
              </div>
            )}

            {/* Uploading State */}
            {uploadState === 'uploading' && (
              <div className="upload-progress-container">
                <div className="file-info">
                  <div className="file-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>
                  <div className="file-details">
                    <span className="file-name">{selectedFile?.name}</span>
                    <span className="file-size">{selectedFile && formatFileSize(selectedFile.size)}</span>
                  </div>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="progress-text">{uploadProgress}%</span>
                </div>
                <p className="uploading-message">Uploading and analyzing your resume...</p>
              </div>
            )}

            {/* Success State - only show if not in animation mode */}
            {uploadState === 'success' && uploadedData && !showAnimation && (
              <div className="upload-success-container">
                <div className="success-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2>Resume Uploaded Successfully!</h2>
                <div className="success-details">
                  <div className="detail-item">
                    <span className="detail-label">File Name</span>
                    <span className="detail-value">{uploadedData.original_filename}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Pages</span>
                    <span className="detail-value">{uploadedData.num_pages}</span>
                  </div>
                  {uploadedData.contact_details?.name && (
                    <div className="detail-item">
                      <span className="detail-label">Name Detected</span>
                      <span className="detail-value">{uploadedData.contact_details.name}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Skills Found</span>
                    <span className="detail-value">{uploadedData.skills_count}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Experience Entries</span>
                    <span className="detail-value">{uploadedData.experience_count}</span>
                  </div>
                </div>
                <button className="continue-button" onClick={startAnimationSequence}>
                  Continue to AI Interview
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>
            )}

            {/* Error State */}
            {uploadState === 'error' && (
              <div className="upload-error-container">
                <div className="error-icon-wrapper">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <h2>Resume upload failed</h2>
                <p className="error-message">
                  {errorMessage || 'Something went wrong. Please check your file and try again.'}
                </p>
                <button className="retry-button" onClick={handleRetry}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  Retry upload
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingResume;
